import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContactsService } from 'app/store/services/contacts.service';
import { UserService } from 'app/store/services/user.service';
import { ButtonDisablerService } from 'app/store/services/button-disabler.service';
import { CONTACT_SAME_DOMAIN_ERROR_CODE, IContact, IContactGroup } from 'app/store/models/contact.model';
import { EInfoSelectItemType, ISelectItem } from 'app/store/models/select.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from 'app/store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { RContentComponent } from '../../dialogs/quick-response/r-content.component';
import { faCheck, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { IUserSessionData, UserType } from '../../store/models/user.model';
import { ELockedBannerState } from 'app/messages/view-message/view-message.component';
import { WebSocketService } from 'app/store/services/web-socket.service';
import { IMessageWSTopicData, MESSAGE_WS_ACTION_LOCK, MESSAGE_WS_ACTION_UNLOCK, MESSAGES_WS_TOPIC } from 'app/store/models/message.model';
import { vmsMessageLock } from 'app/store/actions/user.action';

@Component({
  selector: 'app-create-contact',
  templateUrl: './create-contact.component.html',
  styleUrls: ['./create-contact.component.scss'],
})
export class CreateContactComponent implements OnInit, OnDestroy {
  addButtonProperties: Button = {
    color: ButtonColor.BLUE,
    size: ButtonSize.SMALL,
    text: 'Add',
    onClick: () => this.submitForm(),
  };

  contactGroupsItems: ISelectItem[] = [
    {
      label: 'Add new group',
      value: 0,
    },
  ];

  formGroup: FormGroup;

  componentReady = false;

  emailAddressValid: boolean;
  formSubmissionInProgress: boolean;

  showGroupControl: boolean;

  tooltipText =
    'Select this option if you would like to send a customized email to the contact you are adding. This email will be sent Immediately and the contact will then be scheduled to receive email 2 if associated with an active contact group.';

  state: 'edit' | 'create';

  currentContactId: number;

  emailValidationTriggered: boolean;
  emailValidationInProgress: boolean;

  showAlternativeEmails: boolean;

  canEditContact = true;
  v1 = false;
  v1Label: string;
  v2 = false;
  v2Label: string;
  v3 = false;
  v3Label: string;

  iconQuestionCircle = faQuestionCircle;
  iconCheck = faCheck;
  iconTimes = faTimes;

  disableEmailEdit: boolean;

  userSession: IUserSessionData;

  userTypes = UserType;

  contactData: IContact;
  
  fromMessage: number;

  canViewGroup = true;

  vsmRemainingTime: number;
  vsmRemainingMinutes: number;
  vsmRemainingSeconds: string;
  lockedBannerState: ELockedBannerState = ELockedBannerState.OTHER;
  locketBannerStates = ELockedBannerState;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  private initialEmail: string;
  private groups: IContactGroup[];


  constructor(
    private contactService: ContactsService,
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private userService: UserService,
    private store: Store<IReducers>,
    private router: Router,
    private readonly buttonDisablerService: ButtonDisablerService,
    private readonly wsService: WebSocketService,
  ) {
  }

  get send_email() {
    return this.formGroup.get('send_email');
  }

  toggleAlternativeEmails() {
    if (!this.canEditContact) {
      return false;
    }
    this.showAlternativeEmails = !this.showAlternativeEmails;
  }

  onGroupSelection(value) {
    this.showGroupControl = value === 0;

    if (value === 0) {
      this.formGroup.get('group_name').setValidators(Validators.required);
    }

    if (value === 8591) {
      this.store.dispatch(
        openDialog({
          dialogData: {
            title: `Campaign Email`,
            content: `Warning, you have selected No Campaign. Contacts associated with the No Campaign Group do not receive campaign emails.`,
            dialogMinWidth: '1000px',
            canClose: true,
            buttons: [
              {
                text: 'Ok',
                actAsClose: true,
                color: ButtonColor.GREEN,
                size: ButtonSize.NORMAL,
              },
              {
                text: 'Close',
                actAsClose: true,
                color: ButtonColor.RED,
                size: ButtonSize.NORMAL,
                onClick: () => this.deselectGroup(),
              },
            ],
          },
        }),
      );
    }
  }

  deselectGroup() {
    this.formGroup.get('group_id').setValue('new');
  }

  navigateToContacts(result, form) {
    if (this.activatedRoute.snapshot.queryParams.fromMessage) {
      this.router.navigate(['/messages', 'view', this.activatedRoute.snapshot.queryParams.fromMessage, {
        from: 'inbox',
        vsm_remaining_time: this.vsmRemainingTime,
      }]);
      return;
    }
    if (form.group_id === 0) {
      this.router.navigate([`/user_contacts/${result.group_id}/contacts`]);
    } else {
      this.router.navigate([`/user_contacts/${form.group_id}/contacts`], {
        queryParams: {
          group_name: this.groups.find(g => g.group_id === form.group_id).group_name,
        },
      });
    }
  }

  submitForm() {
    if (this.userSession && this.userSession.user_type === UserType.PARTNER) {
      return this.router.navigate(['/user_contacts', 'user_groups']);
    }
    if (this.formGroup.valid) {
      this.formSubmissionInProgress = true;
      if (this.state === 'create') {
        this.createContact();
      } else {
        const formValue = this.formGroup.value;

        if (!this.showGroupControl) {
          delete formValue.group_name;
        }

        this.contactService
          .updateContact(this.currentContactId, formValue)
          .subscribe(
            result => {
              if (result?.skipped) {
                this.store.dispatch(openSnackbar({
                  snackbarData: {
                    data: {
                      message: 'Contact update skipped due to matching email domain.',
                      type: 'success',
                    },
                  },
                }));
                this.formSubmissionInProgress = false;
                return;
              }
              this.navigateToContacts(result, this.formGroup.value);
            },
            error => this.handleContactSaveError(error, options => {
              this.contactService.updateContact(this.currentContactId, formValue, options)
                .subscribe(
                  retryResult => {
                    if (retryResult?.skipped) {
                      this.store.dispatch(openSnackbar({
                        snackbarData: {
                          data: {
                            message: 'Contact update skipped due to matching email domain.',
                            type: 'success',
                          },
                        },
                      }));
                      this.formSubmissionInProgress = false;
                      return;
                    }
                    this.navigateToContacts(retryResult, this.formGroup.value);
                  },
                  retryError => this.parseErrors(retryError),
                );
            }),
          );
      }
    } else {
      this.buttonDisablerService.disableActive();
      this.formGroup.markAllAsTouched();
      this.store.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: 'Please correct the errors in the form before submitting.',
            type: 'error',
          }
        }
      }));
    }
  }

  parseErrors(error: HttpErrorResponse) {
    if (error && error.error) {
      let errorMessage = null;
      if (typeof error.error.message === 'string') {
        try {
          errorMessage = JSON.parse(error.error.message);
        } catch (e) {
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: error.error.message,
                type: 'error',
              },
            },
          }));
          return;
        }
      } else if (typeof error.error.message === 'object') {
        errorMessage = error.error.message;
      }

      if (errorMessage) {
        for (const name in errorMessage) {
          if (errorMessage[name]) {
            const control = this.formGroup.get(name);

            if (control) {
              control.setErrors({
                validation: errorMessage[name][0],
              });
            }
          }
        }
      }

      this.formGroup.markAllAsTouched();
    }
  }

  getControl(name) {
    return this.formGroup.get(name);
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  loadGroups(): void {
    if (this.contactGroupsItems.length > 0) {
      this.contactGroupsItems = [
        {
          label: 'Add new group',
          value: 0,
        },
      ];
    }

    if(!this.groups || this.groups.length === 0) {
      return;
    }

    this.groups.forEach((group: IContactGroup) =>
      this.contactGroupsItems.push({
        label: group.group_name,
        value: group.group_id,
      }),
    );
  }

  initForm(contactInfo?: IContact) {
    const alternativeEmailsValidators = [];

    if (this.state === 'edit') {
      alternativeEmailsValidators.push(Validators.email);
      // initiate as true on edit
      this.emailAddressValid = true;

      if (contactInfo && contactInfo.email) {
        this.initialEmail = contactInfo.email;
      }
    }

    this.loadGroups();
    this.formGroup = new FormGroup({
      send_email: new FormControl(false),
      group_id: new FormControl(contactInfo ? contactInfo.group_id : '', [
        Validators.required,
      ]),
      group_name: new FormControl(''),
      email: new FormControl(contactInfo ? contactInfo.email : '', [
        Validators.required,
        Validators.email,
      ]),
      alternate_email_1: new FormControl(
        contactInfo ? contactInfo.alternate_email_1 : '',
        alternativeEmailsValidators,
      ),
      alternate_email_2: new FormControl(
        contactInfo ? contactInfo.alternate_email_2 : '',
        alternativeEmailsValidators,
      ),
      alternate_email_3: new FormControl(
        contactInfo ? contactInfo.alternate_email_3 : '',
        alternativeEmailsValidators,
      ),
      firstname: new FormControl(contactInfo ? contactInfo.firstname : ''),
      lastname: new FormControl(contactInfo ? contactInfo.lastname : ''),
      company: new FormControl(contactInfo ? contactInfo.company : ''),
      title: new FormControl(contactInfo ? contactInfo.title : ''),
      private_note: new FormControl(
        contactInfo ? contactInfo.private_note : '',
      ),
      tags: new FormControl(contactInfo ? contactInfo.tags : ''),
      address1: new FormControl(contactInfo ? contactInfo.address1 : ''),
      address2: new FormControl(contactInfo ? contactInfo.address2 : ''),
      city: new FormControl(contactInfo ? contactInfo.city : ''),
      state: new FormControl(contactInfo ? contactInfo.state : ''),
      phone_number: new FormControl(
        contactInfo ? contactInfo.phone_number : '',
      ),
      mobile_phone: new FormControl(
        contactInfo ? contactInfo.mobile_phone : '',
      ),
      fax: new FormControl(contactInfo ? contactInfo.fax : ''),
      zip_code: new FormControl(contactInfo ? contactInfo.zip_code : ''),
      level: new FormControl(contactInfo ? contactInfo.level : ''),
      department1: new FormControl(contactInfo ? contactInfo.department1 : ''),
      department2: new FormControl(contactInfo ? contactInfo.department2 : ''),
      department3: new FormControl(contactInfo ? contactInfo.department3 : ''),
      company_address: new FormControl(
        contactInfo ? contactInfo.company_address : '',
      ),
      company_city: new FormControl(
        contactInfo ? contactInfo.company_city : '',
      ),
      company_state: new FormControl(
        contactInfo ? contactInfo.company_state : '',
      ),
      company_zip: new FormControl(contactInfo ? contactInfo.company_zip : '', [
        Validators.pattern(/^[0-9]*$/),
      ]),
      company_country: new FormControl(
        contactInfo ? contactInfo.company_country : '',
      ),
      company_url: new FormControl(contactInfo ? contactInfo.company_url : ''),
      stock_exchange: new FormControl(
        contactInfo ? contactInfo.stock_exchange : '',
      ),
      ticker_symbol: new FormControl(
        contactInfo ? contactInfo.ticker_symbol : '',
      ),
      revenue: new FormControl(contactInfo ? contactInfo.revenue : ''),
      employees: new FormControl(contactInfo ? contactInfo.employees : ''),
      industry1: new FormControl(contactInfo ? contactInfo.industry1 : ''),
      industry2: new FormControl(contactInfo ? contactInfo.industry2 : ''),
      industry3: new FormControl(contactInfo ? contactInfo.industry3 : ''),
      sub_industry1: new FormControl(
        contactInfo ? contactInfo.subindustry1 : '',
      ),
      sub_industry2: new FormControl(
        contactInfo ? contactInfo.subindustry2 : '',
      ),
      jigsaw_id: new FormControl(contactInfo ? contactInfo.contact_id : ''),
    });

    this.userService.getLabels().subscribe(response => {
      console.log(response);

      if (response.label1) {
        this.v1Label = response.label1.replace(/[{}]/g, '');
        this.formGroup.addControl(
          'variable1',
          new FormControl(
            contactInfo && contactInfo.variable1 ? contactInfo.variable1 : '',
          ),
        );
        this.v1 = true;
      }
      if (response.label2) {
        this.v2Label = response.label2.replace(/[{}]/g, '');
        this.formGroup.addControl(
          'variable2',
          new FormControl(
            contactInfo && contactInfo.variable2 ? contactInfo.variable2 : '',
          ),
        );
        this.v2 = true;
      }
      if (response.label3) {
        this.v3Label = response.label3.replace(/[{}]/g, '');
        this.formGroup.addControl(
          'variable3',
          new FormControl(
            contactInfo && contactInfo.variable3 ? contactInfo.variable3 : '',
          ),
        );
        this.v3 = true;
      }
    });
  }

  private lockMessage(messageId: number) {
    this.wsService.send<IMessageWSTopicData>({
      topic: MESSAGES_WS_TOPIC,
      data: {
        action: MESSAGE_WS_ACTION_LOCK,
        message_id: messageId,
      },
    });
    this.store.dispatch(vmsMessageLock({
      messageId,
    }));
  }

  private unlockMessage(messageId: number): void {
    this.wsService.send<IMessageWSTopicData>({
      topic: MESSAGES_WS_TOPIC,
      data: {
        action: MESSAGE_WS_ACTION_UNLOCK,
        message_id: messageId,
      },
    });
  }

  ngOnInit() {
    this.state = this.activatedRoute.snapshot.data.state;
    this.fromMessage = this.activatedRoute.snapshot.queryParams?.fromMessage || null;
    const queryParams: {
      no_email?: string;
    } = this.activatedRoute.snapshot.queryParams;
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.userSession = sessionData;
        const vsmLockStartTime = this.activatedRoute.snapshot.queryParams?.vsm_lock_start_time || null;
        if (vsmLockStartTime && this.userSession.user_type === UserType.VSM) {
          const elapsed = Date.now() - vsmLockStartTime;
          const lockDuration = 3 * 60 * 1000; // 3 minutes
          if (elapsed < lockDuration) {
            this.lockMessage(this.fromMessage);
            this.lockedBannerState = ELockedBannerState.SELF;
            this.vsmRemainingTime = lockDuration - elapsed;
            this.vsmRemainingMinutes = Math.floor(this.vsmRemainingTime / (60 * 1000));
            this.vsmRemainingSeconds = Math.floor((this.vsmRemainingTime % (60 * 1000)) / 1000).toString();
            if(parseInt(this.vsmRemainingSeconds) < 10) {
              this.vsmRemainingSeconds = '0' + this.vsmRemainingSeconds;
            }
            const intervalId = setInterval(() => {
              this.vsmRemainingTime -= 1000;
              if (this.vsmRemainingTime <= 0) {
                clearInterval(intervalId);
                this.lockedBannerState = ELockedBannerState.OTHER;
                this.unlockMessage(this.fromMessage);
              } else {  
                this.vsmRemainingMinutes = Math.floor(this.vsmRemainingTime / (60 * 1000));
                this.vsmRemainingSeconds = Math.floor((this.vsmRemainingTime % (60 * 1000)) / 1000).toString(); 
                if(parseInt(this.vsmRemainingSeconds) < 10) {
                  this.vsmRemainingSeconds = '0' + this.vsmRemainingSeconds;
                }
              }
            }, 1000);
          } else {
            this.lockedBannerState = ELockedBannerState.OTHER;
          }
          // remove the query params from the url
          this.router.navigate([], {
            queryParams: {
              vsm_lock_start_time: null,
              vsm_remaining_time: null,
            },
            queryParamsHandling: 'merge',
          });
        } else {
          this.lockedBannerState = ELockedBannerState.OTHER;
        }
        this.currentContactId = this.activatedRoute.snapshot.params.id;
        if (sessionData.user_type !== UserType.VSM) {
          this.contactService
            .getContactGroups(sessionData.id)
            .subscribe((groups: IContactGroup[]) => {
              this.groups = groups;
              if (this.state === 'edit') {
                this.contactService
                  .viewContact(this.currentContactId)
                  .subscribe((contact: IContact) => {
                    this.contactData = contact;
                    this.contactData = contact;
                    if (this.contactData.account_id !== sessionData.id) {
                      this.canViewGroup = false;
                    }
                    this.initForm(contact);
                    if (queryParams.no_email && queryParams.no_email === 'true') {
                      this.disableEmailEdit = true;
                      this.formGroup.get('email').disable();
                    }
                    if (contact.account_id !== sessionData.id) {
                      this.formGroup.disable();
                      this.canEditContact = false;
                    }
                    this.componentReady = true;
                  });
              } else {
                this.initForm();
                this.componentReady = true;
              }
            });
        } else {
          this.contactService
            .viewContact(this.currentContactId)
            .subscribe((contact: IContact) => {
              this.contactData = contact;
              if (this.contactData.account_id !== sessionData.id) {
                this.canViewGroup = false;
              }
              this.initForm(contact);
              if (queryParams.no_email && queryParams.no_email === 'true') {
                this.disableEmailEdit = true;
                this.formGroup.get('email').disable();
              }
              if (contact.account_id !== sessionData.id && sessionData.user_type !== UserType.VSM) {
                this.formGroup.disable();
                this.canEditContact = false;
              }
              this.componentReady = true;
            });
        }
      });
  }

  checkNewGroup(value: string) {
    this.contactService
      .checkExistingGroup(value)
      .subscribe((groups: IContactGroup[]) => {
        if (groups.length === 1 && groups[0].group_name === value && this.formGroup.get('contact_id')) {
          this.formGroup.get('contact_id').setValue(groups[0].group_id);
          this.showGroupControl = false;
        }
      });
  }

  checkEmailAddress(value: string) {
    if (
      this.formGroup.get('email').valid &&
      (this.formGroup.get('email').touched || this.formGroup.get('email').dirty)
    ) {
      if (
        this.state === 'edit' &&
        this.initialEmail !== undefined &&
        this.formGroup.get('email').value === this.initialEmail
      ) {
        this.emailValidationInProgress = false;
        this.emailAddressValid = true;
        return;
      }
      this.emailValidationTriggered = true;
      this.emailValidationInProgress = true;
      this.contactService.checkContactEmail(value).subscribe(result => {
        this.emailValidationInProgress = false;
        this.emailAddressValid = !result.exists;
      });
    }
  }

  getSubmitButtonText(): string {
    if (this.userSession && this.userSession.user_type === UserType.PARTNER) {
      return 'Back';
    }
    return this.state === 'edit' ? 'Save' : 'Add';
  }

  resetForm() {
    const selectedGroupId = this.formGroup.get('group_id').value;
    const customGroupName = this.formGroup.get('group_name').value;
    this.formGroup.reset();
    if (selectedGroupId !== 0) {
      this.formGroup.get('group_id').setValue(selectedGroupId);
    } else {
      const targetGroup = this.groups.find(g => g.group_name === customGroupName);
      this.formGroup.get('group_id').setValue(targetGroup.group_id);
      this.formGroup.get('group_id').updateValueAndValidity();
      this.formGroup.get('group_id').markAllAsTouched();
      this.formGroup.get('group_name').removeValidators(Validators.required);
      this.formGroup.get('group_name').updateValueAndValidity();
      this.showGroupControl = false;
    }
    this.formGroup.get('email').markAsPristine();
    this.formGroup.updateValueAndValidity();
    this.emailValidationTriggered = false;
  }

  private createContact() {
    if (this.send_email && this.send_email.value === true) {
      this.validateAndOpenCustomEmailDialog();
      return;
    } else {
      this.contactService.createContact(this.formGroup.value).subscribe(
        result => {
          if (result?.skipped) {
            this.store.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: 'Contact creation skipped due to matching email domain.',
                  type: 'success',
                },
              },
            }));
            this.formSubmissionInProgress = false;
            return;
          }
          this.showConfirmDialog(null, result);
        },
        error => this.handleContactSaveError(error, options => {
          this.contactService.createContact(this.formGroup.value, options)
            .subscribe(
              retryResult => {
                if (retryResult?.skipped) {
                  this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: 'Contact creation skipped due to matching email domain.',
                        type: 'success',
                      },
                    },
                  }));          
                  this.formSubmissionInProgress = false;      
                  return;
                }
                this.showConfirmDialog(null, retryResult);
              },
              retryError => this.parseErrors(retryError),
            );
        }),
      );

    }

  }

  private validateAndOpenCustomEmailDialog(
    sameDomainOptions?: { skip_same_domain_check?: boolean },
  ) {
    this.contactService
      .createContact(this.formGroup.value, { validate_only: true })
      .subscribe(
        () => this.openCustomEmailDialog(sameDomainOptions),
        error => this.handleContactSaveError(error, options => {
          this.openCustomEmailDialog(options);
        }),
      );
  }

  private openCustomEmailDialog(sameDomainOptions?: { skip_same_domain_check?: boolean }) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Custom Email 1',
          content: RContentComponent,
          componentData: {
            data: {
              label: 'Custom Email 1',
              type: EInfoSelectItemType.TYPE_LINK,
              tag: 'custom_email_1',
              contact_email: this.formGroup.get('email').value,
              beforeSend: this.contactService.createContact(this.formGroup.value, sameDomainOptions),
              afterSend: (dialog: DialogComponent, res: { success: boolean }) => {
                dialog.closeDialog();
                this.showConfirmDialog(null, res);
              },
            },
          },
          hideTitleTooltip: true,
          dialogMinWidth: '1000px',
          canClose: true,
        },
      }),
    );
  }

  private showConfirmDialog(dialog: DialogComponent, result: any) {
    this.formSubmissionInProgress = false;
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: `Success`,
          content: `Contact Added Successfully.`,
          dialogMinWidth: '1000px',
          canClose: false,
          buttons: [
            {
              text: 'Add more',
              actAsClose: true,
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: () => {
                if (this.formGroup.get('group_id').value === 0 ) {
                  this.contactService.getContactGroups(this.userSession.id).subscribe(groups => {
                    this.groups = groups;
                    this.loadGroups();
                    this.resetForm();
                  });
                } else {
                  this.resetForm();
                }
              },
            },
            {
              text: 'Ok',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: dialog2 => {
                dialog2.closeDialog();
                this.navigateToContacts(result, this.formGroup.value);

              },
            },
          ],
        },
      }),
    );
  }

  private handleContactSaveError(
    error: HttpErrorResponse,
    onDecision: (options: { skip_same_domain_check?: boolean }) => void,
  ) {
    let message = Array.isArray(error?.error?.message) ? error.error.message[0] : error?.error?.message;
    if (typeof message === 'string' && message.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(message);
        const firstKey = Object.keys(parsed || {})[0];
        const parsedMessage = firstKey ? parsed[firstKey]?.[0] : null;
        if (typeof parsedMessage === 'string') {
          message = parsedMessage;
        }
      } catch (e) {
      }
    }
    const isSameDomainError = error?.error?.code === CONTACT_SAME_DOMAIN_ERROR_CODE;
    if (isSameDomainError) {
      this.store.dispatch(openDialog({
        dialogData: {
          title: 'Check user email domain',
          content: message || "Email address of the contact has a matching domain with your account's email domain.<br/>"
            + 'You can either: Skip the validation and add the contact anyway, or accept the validation and do not create that contact.',
          canClose: false,
          onClose: () => {},
          buttons: [
            {
              text: 'DO NOT CREATE',
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: dialog => {
                dialog.closeDialog();
                this.parseErrors(error);
              },
            },
            {
              text: 'ADD ANYWAY',
              color: ButtonColor.BLUE,
              size: ButtonSize.NORMAL,
              onClick: dialog => {
                dialog.closeDialog();
                onDecision({ skip_same_domain_check: true });
              },
            },
          ],
        },
      }));
      return;
    }
    this.parseErrors(error);
  }
}
